import { Helmet } from 'react-helmet-async';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const SITE_URL = (
    (import.meta.env?.VITE_SITE_URL as string | undefined) ?? 'https://trbd.net'
).replace(/\/$/, '');
export const SITE_NAME = 'TRBD';
export const DEFAULT_LANG = 'ar';
export const TWITTER_HANDLE = '@TRBD';
export const DEFAULT_OG_IMAGE = '/og-image.png';

type SiteLang = 'ar' | 'en';

const OG_LOCALE: Record<SiteLang, string> = {
    ar: 'ar_SY',
    en: 'en_US',
};

export const DEFAULT_LOCALE = OG_LOCALE[DEFAULT_LANG];

export function absoluteUrl(path = '/'): string {
    if (/^https?:\/\//i.test(path)) return path;
    const clean = path.startsWith('/') ? path : `/${path}`;
    return `${SITE_URL}${clean}`;
}

/**
 * Strip any leading `/ar` or `/en` from a path so we can build the lang-specific
 * alternates. Returns a path that always begins with `/`.
 */
function stripLangPrefix(path: string): string {
    const clean = path.startsWith('/') ? path : `/${path}`;
    const match = clean.match(/^\/(ar|en)(\/.*)?$/);
    if (!match) return clean;
    return match[2] || '/';
}

export function localizedUrl(lang: SiteLang, path: string): string {
    const bare = stripLangPrefix(path);
    const suffix = bare === '/' ? '' : bare;
    return `${SITE_URL}/${lang}${suffix}`;
}

export type JsonLd = Record<string, unknown>;

export interface SEOProps {
    title: string;
    description: string;
    path: string;
    keywords?: string | string[];
    ogImage?: string;
    ogType?: 'website' | 'article';
    noindex?: boolean;
    jsonLd?: JsonLd | JsonLd[];
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
    /** Override the active language. Defaults to i18n's current language. */
    lang?: SiteLang;
}


function toKeywordsString(keywords?: string | string[]): string | undefined {
    if (!keywords) return undefined;
    if (Array.isArray(keywords)) return keywords.filter(Boolean).join(', ');
    return keywords;
}

export function SEO({
    title,
    description,
    path,
    keywords,
    ogImage,
    ogType = 'website',
    noindex = false,
    jsonLd,
    publishedTime,
    modifiedTime,
    author,
    section,
    tags,
    lang: langProp,
}: SEOProps) {
    const { i18n } = useTranslation();
    const resolvedLang: SiteLang =
        langProp ?? (i18n.language === 'en' ? 'en' : DEFAULT_LANG);
    const dir = resolvedLang === 'ar' ? 'rtl' : 'ltr';
    const canonical = localizedUrl(resolvedLang, path);
    const arUrl = localizedUrl('ar', path);
    const enUrl = localizedUrl('en', path);
    const image = absoluteUrl(ogImage || DEFAULT_OG_IMAGE);
    const kw = toKeywordsString(keywords);
    const robots = noindex ? 'noindex, nofollow' : 'index, follow';

    const ldScripts = useMemo(() => {
        if (!jsonLd) return [];
        const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
        return arr.filter(Boolean).map((ld) => localizeJsonLd(ld, resolvedLang));
    }, [jsonLd, resolvedLang]);

    return (
        <Helmet>
            <html lang={resolvedLang} dir={dir} />
            <title>{title}</title>
            <meta name="description" content={description} />
            {kw && <meta name="keywords" content={kw} />}
            <meta name="robots" content={robots} />
            <link rel="canonical" href={canonical} />

            <link rel="alternate" hrefLang="ar" href={arUrl} />
            <link rel="alternate" hrefLang="en" href={enUrl} />
            <link rel="alternate" hrefLang="x-default" href={arUrl} />

            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:locale" content={OG_LOCALE[resolvedLang]} />
            <meta property="og:type" content={ogType} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={image} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content={TWITTER_HANDLE} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {ogType === 'article' && publishedTime && (
                <meta property="article:published_time" content={publishedTime} />
            )}
            {ogType === 'article' && modifiedTime && (
                <meta property="article:modified_time" content={modifiedTime} />
            )}
            {ogType === 'article' && author && (
                <meta property="article:author" content={author} />
            )}
            {ogType === 'article' && section && (
                <meta property="article:section" content={section} />
            )}
            {ogType === 'article' &&
                tags?.map((tag, i) => (
                    <meta key={`tag-${i}`} property="article:tag" content={tag} />
                ))}

            {/* Citation-grade meta for AI crawlers (Perplexity, Scholar, Dublin Core indexers) */}
            <meta name="author" content={author || SITE_NAME} />
            <meta name="publisher" content={SITE_NAME} />
            <meta name="rating" content="General" />
            <meta name="dcterms.creator" content={author || SITE_NAME} />
            <meta name="dcterms.publisher" content={SITE_NAME} />
            <meta name="dcterms.language" content={resolvedLang} />
            {ogType === 'article' && <meta name="citation_title" content={title} />}
            {ogType === 'article' && (
                <meta name="citation_author" content={author || SITE_NAME} />
            )}
            {ogType === 'article' && publishedTime && (
                <meta name="citation_publication_date" content={publishedTime} />
            )}
            {ogType === 'article' && publishedTime && (
                <meta name="dcterms.date" content={publishedTime} />
            )}

            {ldScripts.map((ld, i) => (
                <script key={`jsonld-${i}`} type="application/ld+json">
                    {JSON.stringify(ld)}
                </script>
            ))}
        </Helmet>
    );
}

export function breadcrumbJsonLd(
    items: { name: string; path: string }[],
    lang?: SiteLang
): JsonLd {
    const activeLang: SiteLang = lang ?? DEFAULT_LANG;
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            name: item.name,
            item: localizedUrl(activeLang, item.path),
        })),
    };
}

const ORG_DESCRIPTION: Record<SiteLang, string> = {
    ar: 'TRBD — حلول رقمية لتطوير الأعمال. أكثر من 45 مشروعاً و102 عميلاً حول العالم.',
    en: 'TRBD — digital products engineered to last. 45+ projects and 102+ clients worldwide.',
};

export function organizationJsonLd(lang?: SiteLang): JsonLd {
    const activeLang: SiteLang = lang ?? DEFAULT_LANG;
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
        logo: absoluteUrl(DEFAULT_OG_IMAGE),
        description: ORG_DESCRIPTION[activeLang],
        founder: {
            '@type': 'Person',
            name: 'Ahmed Allulu',
            jobTitle: 'CEO & Founder',
        },
        sameAs: [],
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            url: localizedUrl(activeLang, '/contact'),
            availableLanguage: ['ar', 'en'],
        },
    };
}

export function websiteJsonLd(lang?: SiteLang): JsonLd {
    const activeLang: SiteLang = lang ?? DEFAULT_LANG;
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: SITE_URL,
        inLanguage: activeLang,
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${localizedUrl(activeLang, '/blog')}?q={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
        },
    };
}

/**
 * Post-process a caller-supplied JSON-LD object so it is lang-aware without
 * requiring each page to thread `lang` through its helper calls:
 *   - Inject `inLanguage` on schema types that carry localized content.
 *   - Rewrite BreadcrumbList items whose `item` URL has no `/ar|/en` prefix
 *     so they point at the correct lang-specific canonical URL.
 */
function localizeJsonLd(ld: JsonLd, lang: SiteLang): JsonLd {
    if (!ld || typeof ld !== 'object') return ld;
    const type = (ld as Record<string, unknown>)['@type'];
    const out: Record<string, unknown> = { ...(ld as Record<string, unknown>) };

    const LANG_BEARING = new Set([
        'WebSite',
        'CollectionPage',
        'FAQPage',
        'ContactPage',
        'Article',
        'BlogPosting',
        'WebPage',
    ]);

    if (typeof type === 'string' && LANG_BEARING.has(type) && !('inLanguage' in out)) {
        out.inLanguage = lang;
    }

    if (type === 'BreadcrumbList' && Array.isArray(out.itemListElement)) {
        out.itemListElement = (out.itemListElement as Array<Record<string, unknown>>).map((el) => {
            const item = el?.item;
            if (typeof item !== 'string') return el;
            try {
                const u = new URL(item);
                if (/^\/(ar|en)(\/|$)/.test(u.pathname)) return el;
                return { ...el, item: localizedUrl(lang, u.pathname) };
            } catch {
                return el;
            }
        });
    }

    return out;
}
